import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { loadInboxItems, type InboxReviewStatus } from '../inbox/storage';
import { loadOpportunityWorkspace } from '../opportunities/storage';
import { WORKSPACE_SEO } from '../seo/site';
import { useSeo } from '../seo/seo';
import { buildWorkspaceStructuredData } from '../seo/structuredData';
import { loadSignalWorkspace } from '../signals/storage';
import { calculateGrowth, calculatePlanning, calculateProfitability } from '../workspace/calculations';
import { loadBusinessWorkspace } from '../workspace/storage';

function money(value: number): string {
  return `${Math.round(value).toLocaleString('ko-KR')}원`;
}

function daysUntil(value?: string): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  if (!Number.isFinite(parsed)) return null;
  return Math.ceil((parsed - Date.now()) / 86400000);
}

const activeReviewStatuses = new Set<InboxReviewStatus>(['REVIEWING', 'PREPARING']);

export function ProductHomePage() {
  const workspace = useMemo(() => loadBusinessWorkspace(), []);
  const profitability = useMemo(() => calculateProfitability(workspace), [workspace]);
  const planning = useMemo(() => calculatePlanning(workspace), [workspace]);
  const growth = useMemo(() => calculateGrowth(workspace), [workspace]);
  const isStarterWorkspace = workspace.updatedAt === null;
  const queue = useMemo(() => {
    const items = loadInboxItems().filter((item) => item.kind !== 'FRANCHISE');
    const snapshotKeys = new Set(items.map((item) => item.key));
    const watchedKeys = [
      ...loadSignalWorkspace('HOUSING').watchedIds.map((id) => `HOUSING:${id}`),
      ...loadOpportunityWorkspace().watchedIds.map((id) => `BID:${id}`),
      ...loadSignalWorkspace('SUPPORT').watchedIds.map((id) => `SUPPORT:${id}`),
      ...loadSignalWorkspace('SITE').watchedIds.map((id) => `SITE:${id}`),
    ];
    const unresolvedWatched = watchedKeys.filter((key) => !snapshotKeys.has(key)).length;
    const closingSoon = items.filter((item) => {
      const days = daysUntil(item.closesAt);
      return days !== null && days >= 0 && days <= 7 && item.reviewStatus !== 'APPLIED' && item.reviewStatus !== 'HOLD';
    }).length;
    const overdue = items.filter((item) => {
      const days = daysUntil(item.closesAt);
      return days !== null && days < 0 && item.reviewStatus !== 'APPLIED';
    }).length;
    const active = items.filter((item) => activeReviewStatuses.has(item.reviewStatus ?? 'INTEREST')).length;
    const pending = items.filter((item) => (item.reviewStatus ?? 'INTEREST') === 'INTEREST').length + unresolvedWatched;
    return { total: items.length + unresolvedWatched, closingSoon, overdue, active, pending };
  }, []);
  const structuredData = useMemo(() => buildWorkspaceStructuredData(), []);
  useSeo(WORKSPACE_SEO, structuredData);

  return (
    <>
      <section className="suite-hero home-command-hero">
        <div>
          <p className="eyebrow">오늘의 의사결정</p>
          <h1>찾은 정보를 쌓아두지 말고, 오늘 처리할 일로 바꿉니다.</h1>
          <p>공고·지원사업·상권과 청약 정보를 찾고, 관심 후보는 검토 큐에서 비교·준비·완료까지 이어서 관리합니다.</p>
          <div className="home-hero-actions">
            {queue.total > 0
              ? <Link className="action-button action-button-primary" to="/inbox">검토 큐 열기 · {queue.total}건</Link>
              : <Link className="action-button action-button-primary" to="/radar/bids">Radar에서 후보 찾기</Link>}
            <Link className="action-button action-button-secondary" to="/tools/profitability">내 숫자로 사업성 검토</Link>
          </div>
        </div>
        <div className={`suite-workspace-card${isStarterWorkspace ? ' suite-workspace-card-sample' : ''}`}>
          <span>{isStarterWorkspace ? '사업성 Workspace' : '내 Workspace 기준'}</span>
          {isStarterWorkspace ? (
            <>
              <strong>아직 내 숫자가 없습니다.</strong>
              <small>매출·원가·고정비를 입력하면 실제 수익성과 손익분기를 같은 Workspace에서 이어서 볼 수 있습니다.</small>
              <Link className="action-button action-button-secondary" to="/tools/profitability">첫 사업성 분석 시작</Link>
            </>
          ) : (
            <>
              <strong>예상 이익 {money(profitability.profit)}</strong>
              <small>월 예상이익 {money(planning.monthlyProfit)} · 광고 후 {money(growth.estimatedProfitAfterAds)}</small>
              <Link className="action-button action-button-secondary" to="/tools/profitability">가정 다시 검토</Link>
            </>
          )}
        </div>
      </section>

      <section className="home-work-queue" aria-labelledby="home-work-queue-title">
        <div className="home-work-queue-heading">
          <div><p className="eyebrow">검토 큐</p><h2 id="home-work-queue-title">오늘 어디부터 보면 되는지 바로 확인합니다.</h2></div>
          <Link className="action-button action-button-secondary action-button-compact" to="/inbox">전체 검토 큐</Link>
        </div>
        <div className="home-work-grid">
          <Link to="/inbox" className="home-work-card home-work-card-urgent"><span>7일 이내 마감</span><strong>{queue.closingSoon}건</strong><small>완료·보류를 제외한 저장 항목</small></Link>
          <Link to="/inbox" className="home-work-card"><span>검토·준비 중</span><strong>{queue.active}건</strong><small>이미 판단을 시작한 항목</small></Link>
          <Link to="/inbox" className="home-work-card"><span>아직 관심 단계</span><strong>{queue.pending}건</strong><small>검토 시작 여부를 정할 항목</small></Link>
          <Link to="/inbox" className={`home-work-card${queue.overdue > 0 ? ' home-work-card-warning' : ''}`}><span>마감 경과</span><strong>{queue.overdue}건</strong><small>상태를 정리해야 할 항목</small></Link>
        </div>
      </section>

      <section className="suite-section" aria-labelledby="radar-suite-title">
        <div className="suite-section-heading">
          <div><p className="eyebrow">사업 기회 Radar</p><h2 id="radar-suite-title">사업자가 먼저 확인할 정보</h2></div>
          <p>입찰·지원사업·상권을 하나의 흐름으로 보고, 필요한 후보만 검토 큐에 남깁니다.</p>
        </div>
        <div className="suite-card-grid suite-card-grid-three">
          <Link className="suite-card" to="/radar/bids"><span>01</span><strong>공고·입찰</strong><p>발주계획·사전규격·입찰공고에서 실제 참여할 일을 선별합니다.</p><b>지금 참여할 일이 있는가 →</b></Link>
          <Link className="suite-card" to="/radar/support"><span>02</span><strong>지원사업</strong><p>지역과 분야에 맞는 지원사업을 신청 조건과 마감 중심으로 확인합니다.</p><b>받을 수 있는 지원이 있는가 →</b></Link>
          <Link className="suite-card" to="/radar/commercial-areas"><span>03</span><strong>상권 경쟁현황</strong><p>후보 지역의 점포 밀도와 동종업종 표본으로 경쟁 강도를 탐색합니다.</p><b>경쟁 점포가 얼마나 밀집했는가 →</b></Link>
        </div>
      </section>

      <section className="home-secondary-radar" aria-labelledby="asset-radar-title">
        <div>
          <p className="eyebrow">생활·자산 정보</p>
          <h2 id="asset-radar-title">청약정보는 사업 Radar와 구분해서 봅니다.</h2>
          <p>청약Home 공고, 주택형별 공급·분양가·경쟁률과 가능한 경우 주변 실거래 근거까지 확인합니다.</p>
        </div>
        <Link className="action-button action-button-secondary" to="/radar/housing">청약정보 열기</Link>
      </section>

      <section className="suite-section" aria-labelledby="toolkit-suite-title">
        <div className="suite-section-heading"><div><p className="eyebrow">사업 판단 도구</p><h2 id="toolkit-suite-title">후보를 찾은 뒤 숫자로 판단합니다.</h2></div><p>네 도구는 같은 로컬 Workspace를 공유해 숫자를 다시 입력하지 않고 이어서 검토합니다.</p></div>
        <div className="suite-card-grid">
          <Link className="suite-card" to="/tools/profitability"><span>A</span><strong>수익성 분석</strong><p>매출과 전체 비용을 연결해 실제 이익과 마진을 확인합니다.</p><b>이 일을 해도 되는가 →</b></Link>
          <Link className="suite-card" to="/tools/pricing"><span>B</span><strong>가격·견적</strong><p>원가·수수료·목표마진으로 적정 판매가와 할인 하한을 봅니다.</p><b>얼마를 받아야 하는가 →</b></Link>
          <Link className="suite-card" to="/tools/planning"><span>C</span><strong>손익·현금흐름</strong><p>판매량과 고정비를 조정해 BEP·목표이익·런웨이를 확인합니다.</p><b>사업적으로 감당 가능한가 →</b></Link>
          <Link className="suite-card" to="/tools/growth"><span>D</span><strong>광고·성장</strong><p>ROAS, 손익분기 광고수준, 최대 광고비와 목표매출을 계산합니다.</p><b>얼마나 더 키울 수 있는가 →</b></Link>
        </div>
      </section>

      <section className="suite-quick-calculators"><div><h2>한 값만 빨리 계산할 때</h2><p>종합 검토가 아니라 단일 계산만 필요하면 기존 계산기를 사용합니다.</p></div><Link className="action-button action-button-secondary" to="/calculators">빠른 계산기 열기</Link></section>
    </>
  );
}
